<script setup>
    import { formatDateString } from '@/composables/helpers/dates';
    import { useWindowStore } from '@/stores/window';
    import { storeToRefs } from 'pinia';

    const props = defineProps({
        cardImage: {
            type: String,
            required: true,
        },
        altText: {
            type: String,
        },
        headline: {
            type: String,
            required: true,
            default: 'Headline',
        },
        excerpt: {
            type: String,
            default: 'Excerpt',
        },
        category: {
            type: String,
            required: true,
        },
        date: {
            type: [Number, String, Date],
            required: true,
        },
        tags: {
            type: Array,
            required: true,
            default: () => [],
        },
        tagColor: {
            type: String,
            required: false,
        },
        size: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        link: {
            type: String,
            default: '',
        },
        replace: {
            type: Boolean,
            default: false,
        },
    });

    const { innerWidth } = storeToRefs(useWindowStore());
    const postsContainer = ref(null);

    function hideHiddenPosts() {
        if (postsContainer.value) {
            const postsArray = postsContainer.value.children;
            const arr = [...postsArray];
            arr.forEach((tag) => {
                tag.offsetTop > 0 ? tag.setAttribute('aria-hidden', 'true') : tag.setAttribute('aria-hidden', 'false');
            });
        }
    }

    function handleResize() {
        hideHiddenPosts();
    }

    watch(innerWidth, () => {
        handleResize();
    });

    const emit = defineEmits(['clicked']);

    const handleClick = (type, value) => {
        emit('clicked', { type, value });
    };
</script>

<template>
    <section class="blog-card flex" :class="[size, size == 'small' ? 'flex-container-small mb-8' : 'flex-container flex-col']" tabindex="-1">
        <template v-if="!loading">
            <div class="image-container rounded-(--radius) overflow-hidden mb-(--box-padding-tb-m)" style="aspect-ratio: 1.6283524904">
                <MessLink :href="link" v-if="link" :label="headline">
                    <MessImage :src="cardImage" :alt="altText ? altText : 'The featured image for this article.'" />
                </MessLink>
                <MessImage v-else :src="cardImage" :alt="altText ? altText : 'The featured image for this article.'" />
            </div>
            <div class="inner-flex grow flex flex-col">
                <div class="details-container mb-(--box-padding-tb-m) flex items-center">
                    <ProjectTag
                        :link="`/blog?category=${category.toLowerCase()}`"
                        category
                        :useHover="true"
                        :colorSet="tagColor || 'magenta'"
                        :text="category"
                        :replace="replace"
                        @click="handleClick('category', category)"
                        gtmEventName="blog_category_tag_clicked" />
                    <MessHtml tag="span" class="date ml-6 font-hubspot-sans text-[12px] font-medium tracking-[0.84px] uppercase" :html="formatDateString(date)" />
                </div>
                <template v-if="link">
                    <MessLink :href="link" :label="headline">
                        <MessHtml
                            tag="h3"
                            class="headline mb-[1.4rem] normal-case font-hubspot-serif hover:text-(--magenta) transition-colors duration-250"
                            :class="[
                                { 'text-[calc(24/var(--base-fs)*1rem)] font-normal leading-12 tracking-[-0.045rem]': size == 'large' },
                                { 'text-[calc(24/var(--base-fs)*1.2rem)] font-normal leading-[calc(var(--spacing)*9)] tracking-0': size == 'medium' },
                                { 'text-[1.45rem] font-normal leading-[1.925rem] tracking-0': size == 'small' },
                            ]"
                            :html="headline" />
                    </MessLink>
                    <MessHtml
                        v-if="size != 'small' && excerpt"
                        class="excerpt mb-(--box-padding-tb-m) font-hubspot-sans text-[15px] font-light leading-[23px] tracking-[0.3px] line-clamp-3"
                        :html="excerpt"
                        :class="{ 'text-[calc(17/var(--base-fs)*1rem)] leading-7 tracking-[0.02125rem]': size == 'large' }" />
                </template>
                <div v-else>
                    <MessHtml
                        tag="h3"
                        class="headline mb-[1.4rem] normal-case font-hubspot-serif"
                        :class="[
                            { 'text-[calc(24/var(--base-fs)*1rem)] font-normal leading-12 tracking-[-0.045rem]': size == 'large' },
                            { 'text-[calc(24/var(--base-fs)*1.2rem)] font-normal leading-[calc(var(--spacing)*9)] tracking-0': size == 'medium' },
                            { 'text-[1.45rem] font-normal leading-[1.925rem] tracking-0': size == 'small' },
                        ]"
                        :html="headline" />
                    <MessHtml v-if="size != 'small' && excerpt" class="excerpt mb-(--box-padding-tb-m) font-hubspot-sans text-[15px] font-light leading-[23px] tracking-[0.3px] line-clamp-3" :html="excerpt" />
                </div>
                <div class="tag-container mt-auto flex flex-wrap gap-4 row-gap-[0.8rem] max-h-8.25 overflow-clip" v-if="tags.length" ref="postsContainer">
                    <ProjectTag
                        v-for="(tag, index) in tags"
                        colorSet="meteor-transparent"
                        :key="tag"
                        :text="tag"
                        :link="`/blog/?tags=${encodeURIComponent(tag)}`"
                        :useHover="true"
                        :useX="false"
                        @keydown.enter="handleClick('tag', tag)"
                        @click="handleClick('tag', tag)"
                        gtmEventName="blog_tag_clicked" />
                </div>
            </div>
        </template>
        <template v-else>
            <FragmentsCardsSkeleton v-for="i in 12" :key="i" />
        </template>
    </section>
</template>

<style>
    /* [child] — targets child .mess-image rendered by MessImage component */
    .blog-card .mess-image {
        height: auto;
        width: 100%;
        aspect-ratio: 1.6283524904;
        transition: 0.33s ease-in-out;
    }

    /* [state] — dotted underline on blog title hover */
    @media (hover: hover) {
        .blog-card .headline:hover {
            text-decoration: underline dotted #a55c87;
            text-underline-offset: 3px;
            text-decoration-thickness: 1px;
        }
    }

    /* [state] — hover transform on mess-image */
    @media (hover: hover) {
        .blog-card .mess-image:hover {
            transform: scale(var(--scale));
        }
    }

    /* [state] — flex-container-small: side-by-side layout for small cards */
    .blog-card.flex-container-small .image-container {
        width: 37%;
        aspect-ratio: 3/2;
        align-self: flex-start;
        margin-bottom: unset;
    }

    .blog-card.flex-container-small .inner-flex {
        margin-left: 5%;
        width: calc(100% - 37% - 5%);
    }

    .blog-card.flex-container-small .inner-flex .details-container {
        margin-bottom: 1rem;
    }

    .blog-card.flex-container-small .inner-flex .headline {
        margin-bottom: 1.5rem;
    }

    /* [state] — small screen stacking */
    @media (max-width: 680px) {
        .blog-card.flex-container-small {
            flex-direction: column;
        }

        .blog-card.flex-container-small .image-container {
            width: 100%;
            margin-bottom: 1.5rem;
        }

        .blog-card.flex-container-small .inner-flex {
            margin-left: 0;
            width: 100%;
        }
    }

    /* [child] — MessHtml sets display:flow-root which breaks line-clamp; override it */
    .blog-card .excerpt.line-clamp-3 {
        display: -webkit-box;
    }

    /* [calc] — featured-post h3 sizing */
    .blog-card.featured-post h3 {
        font-size: calc(148 / var(--base-fs) * 1rem);
        line-height: 2.5rem;
        letter-spacing: -0.045rem;
    }

    /* [calc] — featured-post excerpt sizing */
    .blog-card.featured-post .excerpt {
        font-size: calc(17 / var(--base-fs) * 1rem);
        font-weight: 300;
        line-height: 1.75rem;
        letter-spacing: 0.02125rem;
    }

    .blog-card .tag-container .project-tag,
    .blog-card .tag-container .project-tag p {
        color: var(--magenta);
        border-radius: 9999px;
        background: transparent;
        font-family: var(--font-hubspot-sans);
        font-weight: 400;
        font-size: 12px;
        line-height: 26px;
        letter-spacing: 0.24px;
        text-decoration: none;
        text-transform: none;
    }

    .blog-card .tag-container .project-tag {
        border: 1px solid var(--magenta);
        padding: 0 10px;
    }

    .blog-card .tag-container .project-tag:hover,
    .blog-card .tag-container .project-tag:focus {
        color: white;
        background: var(--magenta);
    }

    .blog-card .tag-container .project-tag:hover p,
    .blog-card .tag-container .project-tag:focus p {
        color: white;
    }
</style>
